import { useState, useCallback } from 'react';
import bookingApi from '../../../services/bookingApi';
import { BookingConfig } from '../types';
import { useNotification } from '../../../contexts/NotificationContext';

export const useBookingConfig = (smbId: string) => {
  const [config, setConfig] = useState<BookingConfig | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isNewConfig, setIsNewConfig] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const { showNotification } = useNotification();

  const fetchConfig = useCallback(async () => {
    if (!smbId) return;
    setIsLoading(true);
    setError(null);
    setIsNewConfig(false);
    try {
      const data = await bookingApi.getBookingConfig(smbId);
      setConfig(data);
      setIsNewConfig(false);
    } catch (err: any) {
      console.error('Failed to fetch booking config', err);

      const status = err.response?.status;
      const detail = err.response?.data?.detail;
      const message = typeof detail === 'string' ? detail : (err.response?.data?.message || err.message);

      // 404 or 409 means configuration doesn't exist yet - initialize defaults
      if (status === 404 || status === 409) {
        setIsNewConfig(true);

        try {
          const defaultConfig = await bookingApi.getDefaultBookingConfig();
          setConfig(defaultConfig);
        } catch (defaultErr) {
          console.error('Failed to fetch default booking config', defaultErr);
          setConfig(null);
          const errorMsg = 'Failed to load default booking configuration.';
          setError(errorMsg);
          showNotification(errorMsg, 'error');
        }
      } else {
        const errorMsg = message || 'Failed to load booking configuration.';
        setError(errorMsg);
        showNotification(errorMsg, 'error');
      }
    } finally {
      setIsLoading(false);
    }
  }, [smbId, showNotification]);

  const updateConfig = async (payload: Partial<BookingConfig>) => {
    if (!smbId) return false;
    setIsLoading(true);
    try {
      await bookingApi.updateBookingConfig(smbId, payload);
      // PATCH only returns a success acknowledgement. The editor keeps its own
      // optimistic state until the next explicit GET refresh.
      setIsNewConfig(false);
      showNotification('Booking configuration saved successfully', 'success');
      return true;
    } catch (err: any) {
      console.error('Failed to update booking config', err);
      const detail = err.response?.data?.detail;
      const message = typeof detail === 'string' ? detail : (err.response?.data?.detail[0].msg);
      const errorMsg = message || 'Failed to update booking configuration.';
      setError(errorMsg);
      showNotification(errorMsg, 'error');
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  return {
    config,
    isLoading,
    isNewConfig,
    error,
    fetchConfig,
    updateConfig
  };
};
